import { useState, useRef, useEffect } from "react";

const SYSTEM_PROMPT = `You are ARIA (Analytical Root-cause Intelligence Assistant), the dedicated AI analyst for Afribiotech Lean Six Sigma Rotterdam. You are an elite expert in:

**Lean Six Sigma (all belts)**: DMAIC, DMADV, Value Stream Mapping, Kaizen, 5S, Kanban, Poka-Yoke, SMED, TPM, OEE, Takt Time, Flow analysis, Waste elimination (8 wastes: TIMWOODS).

**Root Cause Analysis tools**: 
- 5 Whys (drill deep, never stop at symptoms)
- Fishbone / Ishikawa Diagram (6M: Man, Machine, Method, Material, Measurement, Mother Nature)
- Fault Tree Analysis (FTA)
- Failure Mode and Effects Analysis (FMEA)
- Pareto Analysis (80/20 rule)
- Event & Causal Factor Analysis
- Apollo Root Cause Analysis
- Change Analysis
- Barrier Analysis
- PROACT RCA methodology

**Statistical tools**: SPC, Control Charts, MSA, Process Capability (Cp, Cpk), Regression, DOE, Hypothesis Testing, ANOVA.

**Beyond Lean**: Theory of Constraints (TOC), Agile/Scrum integration, Design Thinking, Systems Thinking, Total Quality Management (TQM), ISO standards.

**Afribiotech context**: You operate for a biotech company in Rotterdam, Netherlands. You understand GMP (Good Manufacturing Practice), FDA/EMA regulations, biotech process validation, cleanroom operations, batch records, deviation management, CAPA (Corrective and Preventive Actions), and pharmaceutical/biotech quality systems.

**Your mission**: 
1. Ask powerful clarifying questions to understand the problem deeply
2. Guide users through structured RCA methodology step-by-step
3. Suggest the most appropriate RCA tools for the situation
4. Help identify TRUE root causes, not just symptoms
5. Recommend corrective and preventive actions
6. Connect findings to Lean Six Sigma improvement opportunities
7. Always think systemically — process, people, technology, culture

**Behavior**:
- Be analytical, precise, and methodical
- Use structured frameworks but explain them accessibly
- Ask "why" relentlessly — push past surface answers
- Format responses clearly with sections, bullets, and tool names where helpful
- Always distinguish between immediate cause, contributing cause, and ROOT cause
- Provide actionable next steps
- When uncertain, ask for more data — "In God we trust; all others bring data" (Deming)

Start every new problem by: (1) Acknowledging the issue, (2) Categorizing it (quality, process, equipment, people, supply chain, etc.), (3) Recommending an RCA approach, (4) Asking your first clarifying question.`;

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  content: `**Welcome to ARIA — Afribiotech LSS Rotterdam**

I'm your AI Root Cause Analysis & Lean Six Sigma analyst, built specifically for Afribiotech Rotterdam operations.

**What I can help you with:**
- 🔍 Root Cause Analysis (5 Whys, Fishbone, FMEA, FTA, Pareto...)
- 📊 Lean Six Sigma (DMAIC, waste elimination, process improvement)
- 🧬 Biotech/GMP deviation & CAPA investigations
- 📈 Statistical process analysis & SPC
- 🏭 Value stream mapping & flow optimization
- ⚙️ Equipment reliability & OEE analysis
- 🔄 Beyond Lean: TOC, Systems Thinking, Design Thinking

**How to start:** Describe any issue, defect, deviation, delay, waste, or problem you're facing. Be as specific as possible — process area, what happened, when, how often, what impact.

*"In God we trust; all others bring data."* — W. Edwards Deming

---
What problem shall we investigate today?`,
};

function formatMessage(text) {
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");
  text = text.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  text = text.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  text = text.replace(/^# (.+)$/gm, "<h1>$1</h1>");
  text = text.replace(/^---$/gm, "<hr/>");
  text = text.replace(/^[•\-] (.+)$/gm, "<li>$1</li>");
  text = text.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);
  text = text.replace(/\n\n/g, "</p><p>");
  text = text.replace(/\n/g, "<br/>");
  return `<p>${text}</p>`;
}

const QUICK_PROMPTS = [
  "Help me do a 5 Whys on a batch failure",
  "Build a Fishbone for a yield loss issue",
  "Start a DMAIC project for our packaging line",
  "We have a recurring contamination event",
  "Analyse waste in our upstream bioprocessing",
  "Help me write a CAPA for an OOS result",
];

export default function App() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  };

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    const userMsg = { id: Date.now(), role: "user", content: userText };
    const newMessages = [...messages.filter((m) => m.id !== "welcome"), userMsg];
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: apiMessages,
        }),
      });

      const data = await response.json();
      const reply =
        data.content?.map((b) => b.text || "").join("") ||
        "I encountered an issue. Please try again.";

      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: "assistant", content: reply },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: "⚠️ Connection error. Please check your network and try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => setMessages([WELCOME_MESSAGE]);

  return (
    <div style={styles.root}>
      <div style={styles.bgPattern} />

      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoMark}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="14" stroke="#00E5C0" strokeWidth="2" fill="none" />
              <path d="M8 16 L14 10 L20 16 L14 22 Z" fill="#00E5C0" opacity="0.8" />
              <circle cx="16" cy="16" r="3" fill="#00E5C0" />
              <line x1="16" y1="2" x2="16" y2="8" stroke="#00E5C0" strokeWidth="1.5" />
              <line x1="16" y1="24" x2="16" y2="30" stroke="#00E5C0" strokeWidth="1.5" />
              <line x1="2" y1="16" x2="8" y2="16" stroke="#00E5C0" strokeWidth="1.5" />
              <line x1="24" y1="16" x2="30" y2="16" stroke="#00E5C0" strokeWidth="1.5" />
            </svg>
          </div>
          <div>
            <div style={styles.brandName}>ARIA</div>
            <div style={styles.brandSub}>Afribiotech · LSS Rotterdam</div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.statusDot} />
          <span style={styles.statusText}>Analyst Online</span>
          <button style={styles.clearBtn} onClick={clearChat}>↺ New</button>
        </div>
      </header>

      <div style={styles.toolStrip}>
        <span style={styles.toolLabel}>Quick Start:</span>
        <div style={styles.quickList}>
          {QUICK_PROMPTS.map((q, i) => (
            <button key={i} style={styles.quickChip} onClick={() => sendMessage(q)} disabled={loading}>
              {q}
            </button>
          ))}
        </div>
      </div>

      <main style={styles.main}>
        {messages.map((msg) => (
          <div key={msg.id} style={{ ...styles.msgRow, justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            {msg.role === "assistant" && (
              <div style={styles.avatar}>
                <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="14" stroke="#00E5C0" strokeWidth="2" fill="none" />
                  <path d="M8 16 L14 10 L20 16 L14 22 Z" fill="#00E5C0" opacity="0.9" />
                  <circle cx="16" cy="16" r="3" fill="#00E5C0" />
                </svg>
              </div>
            )}
            <div style={msg.role === "user" ? styles.userBubble : styles.assistantBubble}>
              {msg.role === "assistant" ? (
                <div style={styles.msgHTML} dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
              ) : (
                <div style={styles.msgText}>{msg.content}</div>
              )}
            </div>
            {msg.role === "user" && <div style={styles.userAvatar}>U</div>}
          </div>
        ))}

        {loading && (
          <div style={{ ...styles.msgRow, justifyContent: "flex-start" }}>
            <div style={styles.avatar}>
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14" stroke="#00E5C0" strokeWidth="2" fill="none" />
                <path d="M8 16 L14 10 L20 16 L14 22 Z" fill="#00E5C0" opacity="0.9" />
                <circle cx="16" cy="16" r="3" fill="#00E5C0" />
              </svg>
            </div>
            <div style={styles.assistantBubble}>
              <div style={styles.thinking}>
                <span style={{ ...styles.dot, animationDelay: "0s" }} />
                <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
                <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
                <span style={styles.thinkingText}>Analysing...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </main>

      <footer style={styles.footer}>
        <div style={styles.inputWrap}>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoResize(); }}
            onKeyDown={handleKey}
            placeholder="Describe your problem, defect, deviation, or process issue…"
            rows={1}
            disabled={loading}
          />
          <button
            style={{ ...styles.sendBtn, opacity: input.trim() && !loading ? 1 : 0.4 }}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div style={styles.footerNote}>
          ARIA · Afribiotech Lean Six Sigma Rotterdam · Press Enter to send, Shift+Enter for new line
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0a1628; }
        ::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 3px; }
        @keyframes blink { 0%,80%,100%{opacity:0.2;transform:scale(0.8)}40%{opacity:1;transform:scale(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)} }
        code{background:rgba(0,229,192,0.12);color:#00E5C0;padding:1px 5px;border-radius:3px;font-family:'IBM Plex Mono',monospace;font-size:.88em}
        h1,h2,h3{color:#00E5C0;margin:8px 0 4px;font-family:'IBM Plex Mono',monospace}
        h1{font-size:1.1em}h2{font-size:1em}h3{font-size:.95em}
        ul{padding-left:1.2em;margin:4px 0}li{margin:2px 0;line-height:1.5}
        hr{border:none;border-top:1px solid rgba(0,229,192,0.2);margin:10px 0}
        strong{color:#7dd3fc}em{color:#a78bfa}
        p{margin:4px 0;line-height:1.6}
      `}</style>
    </div>
  );
}

const styles = {
  root: { display:"flex", flexDirection:"column", height:"100vh", background:"#060d1a", color:"#c8d8e8", fontFamily:"'IBM Plex Sans',sans-serif", position:"relative", overflow:"hidden" },
  bgPattern: { position:"absolute", inset:0, backgroundImage:"radial-gradient(circle at 20% 20%,rgba(0,229,192,0.04) 0%,transparent 50%),radial-gradient(circle at 80% 80%,rgba(59,130,246,0.04) 0%,transparent 50%),repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(0,229,192,0.03) 39px,rgba(0,229,192,0.03) 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(0,229,192,0.03) 39px,rgba(0,229,192,0.03) 40px)", pointerEvents:"none", zIndex:0 },
  header: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", borderBottom:"1px solid rgba(0,229,192,0.15)", background:"rgba(6,13,26,0.95)", backdropFilter:"blur(10px)", zIndex:10, position:"relative" },
  headerLeft: { display:"flex", alignItems:"center", gap:12 },
  logoMark: { display:"flex", alignItems:"center", justifyContent:"center" },
  brandName: { fontFamily:"'IBM Plex Mono',monospace", fontSize:"1.3em", fontWeight:600, color:"#00E5C0", letterSpacing:"0.15em", lineHeight:1.1 },
  brandSub: { fontSize:"0.7em", color:"#4a7fa8", letterSpacing:"0.08em", fontWeight:500 },
  headerRight: { display:"flex", alignItems:"center", gap:8 },
  statusDot: { width:8, height:8, borderRadius:"50%", background:"#00E5C0", boxShadow:"0 0 6px #00E5C0" },
  statusText: { fontSize:"0.72em", color:"#4a7fa8", letterSpacing:"0.05em" },
  clearBtn: { background:"rgba(0,229,192,0.08)", border:"1px solid rgba(0,229,192,0.25)", color:"#00E5C0", borderRadius:6, padding:"4px 10px", fontSize:"0.72em", cursor:"pointer", fontFamily:"'IBM Plex Mono',monospace" },
  toolStrip: { display:"flex", alignItems:"center", gap:10, padding:"8px 16px", borderBottom:"1px solid rgba(0,229,192,0.08)", background:"rgba(6,13,26,0.8)", zIndex:9, overflowX:"auto", flexShrink:0 },
  toolLabel: { fontSize:"0.68em", color:"#4a7fa8", whiteSpace:"nowrap", fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase" },
  quickList: { display:"flex", gap:6, overflowX:"auto" },
  quickChip: { background:"rgba(0,229,192,0.05)", border:"1px solid rgba(0,229,192,0.2)", color:"#7dd3fc", borderRadius:20, padding:"4px 12px", fontSize:"0.68em", cursor:"pointer", whiteSpace:"nowrap", fontFamily:"'IBM Plex Sans',sans-serif" },
  main: { flex:1, overflowY:"auto", padding:"20px 16px", display:"flex", flexDirection:"column", gap:14, zIndex:1, position:"relative" },
  msgRow: { display:"flex", alignItems:"flex-end", gap:8, animation:"fadeUp 0.3s ease forwards" },
  avatar: { width:30, height:30, borderRadius:"50%", background:"rgba(0,229,192,0.08)", border:"1px solid rgba(0,229,192,0.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  userAvatar: { width:30, height:30, borderRadius:"50%", background:"rgba(59,130,246,0.15)", border:"1px solid rgba(59,130,246,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.75em", fontWeight:600, color:"#7dd3fc", flexShrink:0 },
  assistantBubble: { maxWidth:"78%", background:"rgba(10,22,40,0.9)", border:"1px solid rgba(0,229,192,0.15)", borderRadius:"2px 14px 14px 14px", padding:"12px 16px", fontSize:"0.88em", lineHeight:1.65, color:"#c8d8e8", boxShadow:"0 2px 16px rgba(0,0,0,0.3)" },
  userBubble: { maxWidth:"70%", background:"linear-gradient(135deg,rgba(0,116,230,0.25),rgba(59,130,246,0.15))", border:"1px solid rgba(59,130,246,0.3)", borderRadius:"14px 2px 14px 14px", padding:"10px 14px", fontSize:"0.88em", lineHeight:1.6, color:"#e2f0ff" },
  msgText: { whiteSpace:"pre-wrap" },
  msgHTML: { lineHeight:1.65 },
  thinking: { display:"flex", alignItems:"center", gap:6, padding:"2px 0" },
  dot: { width:7, height:7, borderRadius:"50%", background:"#00E5C0", display:"inline-block", animation:"blink 1.2s infinite" },
  thinkingText: { fontSize:"0.78em", color:"#4a7fa8", fontFamily:"'IBM Plex Mono',monospace", letterSpacing:"0.08em", marginLeft:4 },
  footer: { borderTop:"1px solid rgba(0,229,192,0.12)", background:"rgba(6,13,26,0.97)", padding:"12px 16px 10px", zIndex:10, position:"relative" },
  inputWrap: { display:"flex", gap:10, alignItems:"flex-end", background:"rgba(10,22,40,0.8)", border:"1px solid rgba(0,229,192,0.2)", borderRadius:10, padding:"8px 8px 8px 14px" },
  textarea: { flex:1, background:"transparent", border:"none", outline:"none", color:"#c8d8e8", fontSize:"0.9em", fontFamily:"'IBM Plex Sans',sans-serif", lineHeight:1.5, resize:"none", minHeight:"24px", maxHeight:"160px", overflowY:"auto" },
  sendBtn: { width:38, height:38, borderRadius:8, background:"linear-gradient(135deg,#00c9a7,#0074e6)", border:"none", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, cursor:"pointer", boxShadow:"0 2px 8px rgba(0,229,192,0.3)" },
  footerNote: { fontSize:"0.62em", color:"#2a4a68", textAlign:"center", marginTop:8 },
};
